import {
  Component,
  computed,
  inject,
  input,
  OnChanges,
  OnDestroy,
  output,
  signal,
} from '@angular/core'
import { FilterParams } from '../shared/utilities/http-params'
import { IdrColumnMetadata, ReferenceDataSpec, referenceDataSpecs } from './idr-columns'
import { IdrFilterRowComponent, RowChangeEvent } from './idr-filter-row.component'
import { map, Observable, Subscription } from 'rxjs'
import { ReferenceDataService } from '../public/reference-data/reference-data.service'
import { EntityRef } from '../shared/models/referenceData'

const isValid = (row: FilterParams): boolean =>
  Boolean(row.name && row.values[0].value)

const emptyRow: FilterParams = {
  name: '',
  values: [
    { operation: '==', value: '' },
  ],
}

type RefData = Readonly<Record<string, readonly string[]>>

@Component({
  selector: 'app-idr-filters-table',
  imports: [IdrFilterRowComponent],
  template: `
    <div class='root-container'>
      @if (ready()) {
        <h4>
          Filters ({{ filters().length }})
          @if(filters().length == 0) {
            <span class='no-filter-warning'>At least one filter is required</span>
          }
        </h4>
        <div class='scroller'>
          <table class="table">
            <tbody>
              @for (row of rows(); track $index) {
                <tr app-idr-filter-row
                    [row]="row"
                    [selectedFields]="selectedFields()"
                    [columnMetadata]="columnMetadata()"
                    [grabFocus]="row === focusRow()"
                    [values]="getValues(row.name)"
                    (update)="onRowUpdate($event)"
                    (remove)="onRemove($event)"
                >
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    </div>
  `,
  styles: `
    .root-container {
      display: flex;
      flex-direction: column;
      height: calc(100vh - 200px);
      
      h4 {
        .no-filter-warning {
          color: #997404;
          float: right;
          margin-right: 2rem;
        }
      }
      
      table {
        width: 100%;
        table-layout: fixed;
      }
    }
    
    .status {
      padding: 1rem 0.5rem 1rem 0.5rem;
    }

    .scroller {
      flex-basis: 0;
      flex-grow: 1;
      overflow-y: auto;
    }
  `,
})
export class IdrFiltersTableComponent implements OnChanges, OnDestroy {
  readonly filters = input.required<readonly FilterParams[]>()
  readonly columnMetadata = input.required<IdrColumnMetadata>()
  readonly ready = input.required<boolean>()
  readonly editedFilter = input<string>()
  readonly editedFilterReset = output()
  private editedFilterResetPending = false
  readonly filtersChange = output<readonly FilterParams[]>()

  private readonly subscription: Subscription = new Subscription()
  private readonly referenceDataService = inject(ReferenceDataService)
  protected readonly refData = signal<RefData>({})

  protected readonly rows = signal<readonly FilterParams[]>([])

  protected readonly selectedFields = computed<readonly string[]>(() =>
    this.rows().map(_ => _.name).filter(Boolean),
  )
  protected readonly focusRow = computed<FilterParams | undefined>(() => {
    const field = this.editedFilter()
    return field ? this.rows().find(_ => _.name === field) : undefined
  })

  private setRefData (field: string, values: readonly string[]) {
    this.refData.set({
      ...this.refData(),
      [field]: values,
    })
  }

  private addSubscription(field: string, observable: Observable<readonly string[]>) {
    this.subscription.add(observable.subscribe(values => {
      // without this angular throws an NG0600 error... maybe because of caching
      // so ensure that this is always asynchronous
      window.setTimeout(() => {
        this.setRefData(field, values)
      }, 0)
    }))
  }

  protected getValues(field: string): readonly string[] | undefined {
    const values = this.refData()[field]
    if (values) {
      return values
    } else {
      const getEntityValues = (
        observable: Observable<EntityRef[]>,
        property: ReferenceDataSpec['entityProperty'],
      ): readonly string[] => {
        this.addSubscription(field, observable.pipe(map(entities => entities.map(e => e[property]))))
        return []
      }

      const refDataSpec = referenceDataSpecs[field]
      if (!refDataSpec) {
        return undefined
      } else if (refDataSpec.aliasFor) {
        return this.getValues(refDataSpec.aliasFor)
      } else {
        return getEntityValues(this.referenceDataService.data(refDataSpec.source), refDataSpec.entityProperty)
      }
    }
  }

  protected onRowUpdate = ({ oldRow, newRow }: RowChangeEvent): void => {
    this.applyChanges(this.rows().map(r => r === oldRow ? newRow : r))
  }

  protected onRemove(row: FilterParams) {
    this.applyChanges(this.rows().filter(r => r !== row))
  }

  private applyChanges = (rows: FilterParams[]) => {
    const filters = rows.filter(isValid)
    this.rows.set(filters.length === rows.length ? [...rows, emptyRow] : rows)
    this.filtersChange.emit(filters)
  }

  ngOnChanges() {
    this.maybeInitializeRows()
    this.maybeApplyEditedFilter()
  }

  private maybeAddFilterFor(field: string) {
    const rows = this.rows()
    if (!rows.some(_ => _.name === field) && rows.includes(emptyRow)) {
      this.rows.set(rows.map(r => r === emptyRow ? { ...r, name: field } : r))
    }
  }

  private maybeInitializeRows() {
    if (this.rows().length <= this.filters().length) {
      this.rows.set([ ...this.filters(), emptyRow ])
    }
  }

  private maybeApplyEditedFilter() {
    const field = this.editedFilter()
    if (field && !this.editedFilterResetPending) {
      this.maybeAddFilterFor(field)
      this.resetEditedFilter()
    }
  }

  private resetEditedFilter(): void {
    this.editedFilterResetPending = true
    window.setTimeout(() => {
      this.editedFilterResetPending = false
      this.editedFilterReset.emit()
    })
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe()
  }
}
